import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { useMutation } from 'convex/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

export const COVER_ACCEPT = 'image/*,video/mp4,video/webm,video/quicktime'
const MAX_IMAGE = 10 * 1024 * 1024
const MAX_VIDEO = 50 * 1024 * 1024
const IMAGE = /^image\/(jpeg|png|webp|gif)$/
const VIDEO = /^video\/(mp4|webm|quicktime)$/

export function pickCoverFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = COVER_ACCEPT
    input.onchange = () => resolve(input.files?.[0] ?? null)
    input.oncancel = () => resolve(null)
    input.click()
  })
}

// A frame ~1s in (or the middle of a short clip), as a JPEG.
async function capturePoster(file: File): Promise<Blob | null> {
  const url = URL.createObjectURL(file)
  try {
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.preload = 'auto'
    video.src = url
    await new Promise<void>((res, rej) => {
      video.onloadeddata = () => res()
      video.onerror = () => rej(new Error('load'))
    })
    video.currentTime = Math.min(1, (video.duration || 2) / 2)
    await new Promise<void>((res) => {
      video.onseeked = () => res()
    })
    const scale = Math.min(1, 1280 / Math.max(video.videoWidth, video.videoHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(video.videoWidth * scale)
    canvas.height = Math.round(video.videoHeight * scale)
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height)
    return await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.85))
  } catch {
    return null
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function useUploadCover() {
  const { t } = useTranslation()
  const getUrl = useMutation(api.nodes.generateCoverUploadUrl)
  const setCover = useMutation(api.nodes.setUploadedCover)
  const setPoster = useMutation(api.nodes.setCoverPoster)

  async function post(blob: Blob) {
    const res = await fetch(await getUrl({}), {
      method: 'POST',
      headers: { 'Content-Type': blob.type },
      body: blob,
    })
    if (!res.ok) throw new Error('upload')
    return (await res.json()).storageId as Id<'_storage'>
  }

  return async function uploadCover(goalId: Id<'nodes'>) {
    const file = await pickCoverFile()
    if (!file) return
    const isVideo = VIDEO.test(file.type)
    if (!isVideo && !IMAGE.test(file.type)) return void toast.error(t('common.coverBadType'))
    if (file.size > (isVideo ? MAX_VIDEO : MAX_IMAGE))
      return void toast.error(
        t(isVideo ? 'common.coverVideoTooLarge' : 'common.coverImageTooLarge'),
      )
    const id = toast.loading(t('common.uploadingCover'))
    try {
      const res = await setCover({ goalId, storageId: await post(file) })
      if (!res.ok) throw new Error(res.error)
      if (isVideo) {
        const poster = await capturePoster(file)
        if (poster) await setPoster({ goalId, storageId: await post(poster) }).catch(() => {})
      }
      toast.success(t('common.coverUploaded'), { id })
    } catch {
      toast.error(t('common.coverUploadFailed'), { id })
    }
  }
}
